<template>
  <div :class="$style.root">
    <div ref="carousel" :class="$style.carousel">
      <div :class="$style.container">
        <div v-for="store in parallelList" :key="store.document_id" :class="$style.slide">
          <ParallelListItem :store="store" />
        </div>
      </div>
    </div>
    <div :class="$style.buttons">
      <AtomsConsumerButton :class="[$style.button, $style.prev]" @click="emblaApi?.scrollPrev()" />
      <AtomsConsumerButton :class="$style.button" @click="emblaApi?.scrollNext()" />
    </div>
    <div :class="$style.dots">
      <div
        v-for="(item, index) in parallelList"
        :key="item.document_id"
        :class="[$style.dot, { [$style.active]: slidesIndex === index }]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import emblaCarouselVue from "embla-carousel-vue"
import Autoplay from "embla-carousel-autoplay"
import ParallelListItem from "./ParallelListItem.vue"
import { StoreFragment } from "~/types/type.generated"

const props = defineProps<{
  isMd: boolean
  parallelList: StoreFragment[]
}>()

const dotsSelectedIndex = ref<number>(0)

const slidesIndex = computed(() => (props.isMd ? dotsSelectedIndex.value * 2 : dotsSelectedIndex.value))

const slidesToScroll = computed(() => (props.isMd ? 2 : "auto"))
const [carousel, emblaApi] = emblaCarouselVue({ loop: true, slidesToScroll: slidesToScroll.value }, [
  Autoplay({ delay: 5000 }),
])

watchEffect(() => {
  if (emblaApi.value) {
    // 初回のインデックスを設定
    dotsSelectedIndex.value = emblaApi.value.selectedScrollSnap()

    // autoplay, 手動でスライドを変更した際イベントをハンドリング -> インデックス更新
    emblaApi.value.on("select", (event) => {
      dotsSelectedIndex.value = event.selectedScrollSnap()
    })
  }
})
</script>

<style scoped module lang="scss">
.root {
  position: relative;
  margin: 0 -1rem;
  .carousel {
    overflow: hidden;
    @include md {
      margin: 0 2rem;
    }
    .container {
      display: flex;
      .slide {
        flex: 0 0 80%;
        margin: 0 0.5rem;
        cursor: pointer;
        img {
          width: 100%;
          object-fit: cover;
        }
        @include md {
          margin: 0 1rem;
          flex: 0 0 calc(50% - 1rem);
          img {
            aspect-ratio: 1220 / 620;
          }
        }
      }
    }
  }

  .buttons {
    display: none;
    @include md {
      display: block;
      position: absolute;
      width: 100%;
      display: flex;
      justify-content: space-between;
      padding: 0;
      pointer-events: none;
      top: 30%;
    }

    .button {
      display: block;
      width: 32px;
      height: 32px;
      background: url("/images/common/arrow/arrow-secondary.svg") no-repeat center $white;
      border-radius: 50%;
      z-index: 1;
      pointer-events: auto;
      transform: translateY(-50%);
      &.prev {
        transform: translateY(-50%) scale(-1, 1);
      }
      @include md {
        width: 56px;
        height: 56px;
      }
    }
  }

  .dots {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    width: 100%;

    @include md {
      gap: 0.5rem;
    }
    .dot {
      width: 100%;
      height: 0.25rem;
      background-color: $primary-20;

      @include md {
        width: 3.75rem;
        border-radius: 4px;
      }

      &.active {
        background-color: $primary;
      }
    }
  }
}
</style>
